import { Drawer } from "@/components/drawer";
import { WordManagerFormLocale } from "@/locales/localeid";
import { VCSLevelModel } from "@/models/title-list-model";
import { WordSpecVpcRespnseModel } from "@/models/word-manager-model";
import { VersionGroupModule } from "@/modules/versiongroup";
import { WordManagerService } from "@/services/word-manager-service";
import { CurriculumType, MediaPlayerContentType, MediaSlot, MediaStatus, MediaType } from "@/util/enum";
import {
	Button,
	Checkbox,
	Col,
	FormModel,
	Icon,
	Input,
	InputNumber,
	Row,
	Select,
	Upload,
	Divider,
	Progress
} from "ant-design-vue";
import {
	Action,
	ActionItem,
	MatIcon,
	MessageHelper,
	NotificationType,
	PrimaryLanguageLocale,
} from "commonui";
import { cloneDeep, isString, last, sortBy } from "lodash";
import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import IPdfPagesViewer from "../pdf-pages-viewer/pdf-pages-viewer";
import PdfPagesViewer from "../pdf-pages-viewer/pdf-pages-viewer.vue";
import VideoJSPlayer from "@/components/videojsplayer/videojsplayer.vue";
import IVideoJSPlayer from "@/components/videojsplayer/videojsplayer";
import { ResourceService } from "@/services/resource";
import { MediaSlotBinding } from "@/views/text-guardian/sub-views/title-list/sub-views/media/utils/const";
import { getModule } from "vuex-module-decorators";
import { MediaUploadState } from "@/modules/media-upload";
import { v4 } from "uuid";
import Spinning from "@/components/spinning/spinning.vue";

@Component({
	components: {
		Button,
		Drawer,
		FormModel,
		FormModelItem: FormModel.Item,
		Select,
		Option: Select.Option,
		Input,
		InputNumber,
		Checkbox,
		MatIcon,
		Row,
		Col,
		Icon,
		Action,
		ActionItem,
		Upload,
		PdfPagesViewer,
		VideoJSPlayer,
		Spinning,
		Divider,
		Progress
	}
})
export default class WordManagerFormVpc extends Vue {
	@Prop({ default: 0 }) wordId!: number;
	@Prop({ default: [] }) levels!: VCSLevelModel[];
	@Prop({ default: null }) selectedGSVersionId!: string;
	@Prop({ default: null }) selectedLSVersionId!: string;

	@Ref("formRef") formRef!: FormModel;
	@Ref("pdfPagesViewerRef") pdfPagesViewerRef!: IPdfPagesViewer;
	@Ref("videoPlayerRef") videoPlayerRef!: IVideoJSPlayer;

	public visible: boolean = false;
	private vpcs: WordSpecVpcRespnseModel[] = [];
	private mediaUploadModule = getModule(MediaUploadState);

	public originalItems: WordSpecVpcItem[] = [];
	public form: { items: WordSpecVpcItem[] } = { items: [] };
	public unitOccupied: { [key: number]: boolean } = {};

	public loading: boolean = false;

	public locale = WordManagerFormLocale;
	public MediaStatus = MediaStatus;
	public mediaSlot = MediaSlot;

	public saveLoading: boolean = false;

	public mediaSlotInfo: {
		[key: number]: {
			ext: string;
			icon: string;
			text: string;
			tooltip: string;
			handler: (item: WordSpecVpcItemMedia) => void;
		};
	} = {
		[MediaSlot.Audio]: {
			ext: ".mp3",
			icon: "audiotrack",
			text: "Audio", // localize
			tooltip: "Play Audio", // localize
			handler: this.viewMedia
		},
		[MediaSlot.ViewingContent]: {
			ext: ".pdf",
			icon: "picture_as_pdf",
			text: "Viewing Content", // localize
			tooltip: "View Pdf", //localize
			handler: this.viewMedia
		}
	};

	mounted() {
		this.setWordVpcInfo();
	}

	@Watch("wordId")
	onWordChange() {
		this.setWordVpcInfo();
	}

	public get vpcCount(): string {
		if (this.originalItems.length) {
			return `(${this.originalItems.length})`;
		}
		return "";
	}

	public isFile(fileUrl?: string | File | null): boolean {
		return fileUrl instanceof File;
	}
	public getFileName(fileUrl: string | File | null): string {
		if (fileUrl instanceof File) {
			return fileUrl.name;
		}
		return "";
	}

	private checkMediaInitialState() {
		this.vpcs.forEach((vpc) => {
			if (vpc.content) {
				const media = vpc.content.media;
				// Check for Viewing Content
				vpc.content.hasViewingContentMedia =
					media.filter((m) => m.mediaSlotId === MediaSlot.ViewingContent).length > 0;
				// Check for Audio Content
				vpc.content.hasAudioMedia = media.filter((m) => m.mediaSlotId === MediaSlot.Audio).length > 0;
			}
		});
	}

	private setWordVpcInfo() {
		if (this.wordId && !(this.selectedGSVersionId === "-1" && this.selectedLSVersionId === "-1")) {
			this.loading = true;
			WordManagerService.getWordVpcInfo(this.wordId, this.selectedGSVersionId, this.selectedLSVersionId)
				.then((data) => {
					this.vpcs = data;
					// Check initial state for presence of Viewing Content and Audio
					this.checkMediaInitialState();
					this.originalItems = data
						.sort((a, b) => (a.levelId < b.levelId ? -1 : 1))
						.map(this.generateNewItem);
					this.loading = false;
					this.setFormData();
				})
				.catch(() => {
					this.loading = false;
				});
		} else {
			this.originalItems = [];
			this.setFormData();
		}
	}

	private getVpcVersionGroupName(vpcMedia: WordSpecVpcItemMedia[], unitId?: number) {
		const curriculumType = this.levels.find((l) => l.id === unitId)?.curriculumType;
		if (curriculumType === CurriculumType.GrapeSEED) {
			return (
				VersionGroupModule.versionGroups.find((vg) => vg.id === this.selectedGSVersionId)?.name ||
				this.$format(this.locale.WordManagerFormVpcUsageVGNoneText)
			);
		} else {
			return (
				VersionGroupModule.versionGroups.find((vg) => vg.id === this.selectedLSVersionId)?.name ||
				this.$format(this.locale.WordManagerFormVpcUsageVGNoneText)
			);
		}
	}

	private getFormattedVpcMedia(
		vpcMedia?: WordSpecVpcItemMedia[],
		vpcId?: number
	): [WordSpecVpcItemMedia, WordSpecVpcItemMedia] {
		const audioSlot: WordSpecVpcItemMedia = {
			fileUrl: null,
			mediaId: "",
			mediaStatusId: MediaStatus.Processing,
			isMediaReplaced: false,
			isMediaRemoved: false,
			isMediaAdded: false,
			mediaSlotId: MediaSlot.Audio,
			vpcId: vpcId,
			isUploading: false,
			fileId: v4(),
			size: 0
		};
		const viewingContentSlot: WordSpecVpcItemMedia = {
			fileUrl: null,
			mediaId: "",
			mediaStatusId: MediaStatus.Processing,
			isMediaReplaced: false,
			isMediaRemoved: false,
			isMediaAdded: false,
			mediaSlotId: MediaSlot.ViewingContent,
			vpcId: vpcId,
			isUploading: false,
			fileId: v4(),
			size: 0
		};

		if (vpcMedia) {
			vpcMedia.forEach((media) => {
				if (media.mediaSlotId === MediaSlot.Audio) {
					audioSlot.fileUrl = media.fileUrl;
					audioSlot.mediaId = media.mediaId;
					audioSlot.mediaStatusId = media.mediaStatusId;
				} else if (media.mediaSlotId === MediaSlot.ViewingContent) {
					viewingContentSlot.fileUrl = media.fileUrl;
					viewingContentSlot.mediaId = media.mediaId;
					viewingContentSlot.mediaStatusId = media.mediaStatusId;
				}
			});
		}
		return [viewingContentSlot, audioSlot];
	}

	private getVpcValues(vpcs: WordSpecVpcItem[]): WordSpecVpcItem[] {
		return vpcs.map((vpc) => {
			return {
				id: vpc.id,
				wordSpecId: vpc.wordSpecId,
				levelId: vpc.levelId,
				sequence: vpc.sequence,
				isReview: vpc.isReview,
				unit: vpc.unit,
				versionGroupId: vpc.versionGroupId,
				media: this.getFormattedVpcMedia(vpc.media, vpc.id),
				versionGroupName: this.getVpcVersionGroupName(vpc.media, vpc.levelId)
			};
		});
	}

	private setFormData() {
		this.form.items = this.getVpcValues(this.originalItems);
		this.occupiedUnitUpdate();
	}

	private generateNewItem(vpc?: WordSpecVpcRespnseModel): WordSpecVpcItem {
		const media = vpc?.content?.media;
		return {
			id: vpc?.id || undefined,
			levelId: vpc?.levelId || this.getUnitForNewVpc(),
			sequence: vpc?.sequence || 1,
			isReview: vpc?.isReview || false,
			wordSpecId: this.wordId,
			media: media
				? (media?.map((vpcMedia) => ({
						fileUrl: vpcMedia.url,
						mediaId: vpcMedia.id,
						mediaStatusId: vpcMedia.mediaStatusId,
						isMediaReplaced: false,
						isMediaRemoved: false,
						mediaSlotId: vpcMedia.mediaSlotId
				  })) as WordSpecVpcItemMedia[])
				: this.getFormattedVpcMedia(media),
			versionGroupName: this.getVPCVersionGroupNameNewItem(vpc?.levelId || this.getUnitForNewVpc())
		};
	}

	private getVPCVersionGroupNameNewItem(unitId) {
		const curriculumType = this.levels.find((l) => l.id === unitId)?.curriculumType;
		if (curriculumType === CurriculumType.GrapeSEED) {
			return (
				VersionGroupModule.versionGroups.find((vg) => vg.id === this.selectedGSVersionId)?.name ||
				this.$format(this.locale.WordManagerFormVpcUsageVGNoneText)
			);
		} else {
			return (
				VersionGroupModule.versionGroups.find((vg) => vg.id === this.selectedLSVersionId)?.name ||
				this.$format(this.locale.WordManagerFormVpcUsageVGNoneText)
			);
		}
	}

	private getUnitForNewVpc(unitId?: number): number {
		const sortedUnits = sortBy(this.levels, "curriculumType", "unit");

		const lastUnitId = unitId ? unitId : last(this.form.items)?.levelId;
		const indexOfLastVpcUnit = sortedUnits.findIndex((level) => level.id === lastUnitId);
		const isLastUnit = indexOfLastVpcUnit === sortedUnits.length - 1;

		let nextUnitId: number | null = null;
		if (isLastUnit) {
			nextUnitId = sortedUnits[0].id;
		} else {
			nextUnitId = sortedUnits[indexOfLastVpcUnit + 1].id;
		}

		const isNextUnitIdAlreadyUsed = this.unitOccupied[nextUnitId];
		if (!isNextUnitIdAlreadyUsed) {
			return nextUnitId;
		} else {
			return this.getUnitForNewVpc(nextUnitId);
		}
	}

	public addVpc() {
		this.form.items.push(this.generateNewItem());
		this.occupiedUnitUpdate();
	}

	public removeVpc(index: number) {
		this.form.items = this.form.items.filter((_, i) => i !== index);
		this.occupiedUnitUpdate();
	}

	public occupiedUnitUpdate(item?) {
		const newUnitOccupied = {};
		this.form.items.forEach((item) => {
			if (item.levelId) {
				newUnitOccupied[item.levelId] = true;
			}
		});
		this.unitOccupied = newUnitOccupied;

		if (item) {
			this.form.items.forEach((obj) => {
				if (obj.levelId == item.levelId) {
					obj.versionGroupName = this.getVPCVersionGroupNameNewItem(obj.levelId);
				}
			});
		}
	}

	private getVersionGroupIdOfUnit(unitId: number) {
		const unit = this.levels.find((level) => level.id === unitId)!;
		let versionGroupId!: string;
		if (unit.curriculumType === CurriculumType.GrapeSEED) {
			versionGroupId = VersionGroupModule.selectedGsVersion;
		} else {
			versionGroupId = VersionGroupModule.selectedLsVersion;
		}
		return { versionGroupId, unit: unit.unit };
	}

	public addMedia($event: { file: File }, item: WordSpecVpcItemMedia) {
		const { file } = $event;

		if (file && this.validateFile(file, item.mediaSlotId)) {
			// Find the concerned VPC using item.vpcId
			const vpc = this.vpcs.find((v) => v.id === item.vpcId);
			// Identify the media type: Viewing Content or Audio based on mediaSlotId
			const isViewingContent = item.mediaSlotId === MediaSlot.ViewingContent;
			const isAudioContent = item.mediaSlotId === MediaSlot.Audio;

			// Get the initial state of media from the found VPC
			const isMediaInitiallyPresent = isViewingContent
				? vpc?.content?.hasViewingContentMedia
				: isAudioContent
				? vpc?.content?.hasAudioMedia
				: false;

			if (item.isMediaRemoved) {
				// If media was removed earlier, check the initial state
				if (isMediaInitiallyPresent) {
					// If it was initially present, this is a modification (remove -> add)
					item.isMediaReplaced = true;
					item.isMediaAdded = false;
				} else {
					// If it was not initially present, it's a net addition (add -> remove -> add)
					item.isMediaAdded = true;
					item.isMediaReplaced = false;
					item.fileId = v4();
				}
				item.isMediaRemoved = false;
				item.fileUrl = file;
			} else if (item.isMediaReplaced || (isString(item.fileUrl) && !item.isMediaAdded)) {
				// Handle media replacement case
				item.isMediaReplaced = true;
				item.isMediaAdded = false;
				item.isMediaRemoved = false;
				item.fileUrl = file;
			} else {
				// Handle media addition case
				item.isMediaAdded = true;
				item.isMediaReplaced = false;
				item.isMediaRemoved = false;
				item.fileUrl = file;
				item.fileId = v4();
			}
		}
	}

	private validateFile(file: File, mediaSlot: MediaSlot): boolean {
		const fileExtension = file.name.split(".").pop()!;

		const expectedExtension = this.mediaSlotInfo[mediaSlot].ext;
		if (expectedExtension === `.${fileExtension}`) {
			return true;
		}

		MessageHelper.Message(
			NotificationType.Warning,
			this.$format(this.locale.WordManagerFormVpcUsageMediaInvalid, { extension: expectedExtension })
		);

		return false;
	}

	public removeMedia(item: WordSpecVpcItem, mediaIndex: number) {
		const vpcMedia = item.media[mediaIndex];
		vpcMedia.isMediaRemoved = true;
		vpcMedia.fileUrl = null;
		vpcMedia.fileId = undefined;
		vpcMedia.isMediaAdded = false;
		vpcMedia.isMediaReplaced = false;
	}

	public async viewMedia(item?: WordSpecVpcItemMedia) {
		if (item?.mediaSlotId === MediaSlot.Audio) {
			if (isString(item?.fileUrl) && item?.mediaId) {
				const fileUrl = await ResourceService.getDownloadMediaUrl(item.mediaId);
				const result = this.videoPlayerRef.showModal(item.mediaId as string, MediaType.MP3, "Media Viewer");
				if (result) {
					setTimeout(() => {
						this.videoPlayerRef.setAWSMediaPlayer(
							item.mediaId as string,
							fileUrl,
							MediaPlayerContentType.mp3
						);
					}, 100);
				}
			}
		} else {
			if (isString(item?.fileUrl) && item?.mediaId) {
				this.pdfPagesViewerRef.open({
					mediaId: item.mediaId,
					mediaSlotId: MediaSlot.ViewingContent
				});
			}
		}
	}

	public open() {
		if (!this.vpcs.length && !this.form.items.length) {
			this.form.items.push(this.generateNewItem());
		}
		this.visible = true;
	}

	public cancel() {
		this.visible = false;
		this.setFormData();
	}

	private uploadFiles(): Promise<void> {
		return new Promise((resolve, reject) => {
			const vpcs = this.form.items;
			const filesToUpload: Promise<void>[] = [];

			vpcs.forEach((vpc) => {
				vpc.media.forEach((media) => {
					if (media.fileUrl instanceof File) {
						const uploaServiceCall = this.mediaUploadModule
							.uploadFile({
								id: media.fileId!,
								file: media.fileUrl!,
								slotId: media.mediaSlotId,
								languageCode: PrimaryLanguageLocale.English
							})
							.then(() => {
								const uploadedFile = this.mediaUploadModule.fileUploads.find(
									(file) => file.id === media.fileId
								)!;

								media.fileUrl = uploadedFile.filePath;
								media.size = uploadedFile.size;
								media.isUploading = false;
							});
						media.isUploading = true;
						filesToUpload.push(uploaServiceCall);
					}
				});
			});

			if (filesToUpload.length) {
				this.saveLoading = true;
				return Promise.all(filesToUpload)
					.then(() => {
						this.saveLoading = false;
						resolve();
					})
					.catch(() => {
						reject();
						this.saveLoading = false;
					});
			} else {
				resolve();
			}
		});
	}

	public save() {
		this.formRef.validate((isValid) => {
			if (isValid) {
				this.uploadFiles()
					.then(() => {
						this.originalItems = cloneDeep(this.form.items);
						this.visible = false;
					})
					.catch(() => {
						console.error("File upload error...");
					});
			}
		});
	}

	public getVpcs() {
		const vpcs = cloneDeep(this.form.items);
		vpcs.forEach((vpc) => {
			const { versionGroupId, unit } = this.getVersionGroupIdOfUnit(vpc.levelId!);

			vpc.versionGroupId = versionGroupId;
			vpc.unit = unit;

			vpc.media?.forEach((vpcMedia) => {
				delete vpcMedia.mediaStatusId;
			});
		});
		return vpcs;
	}

	public getIcon(fileUrl?: string | File | null) {
		const file = isString(fileUrl) ? fileUrl : fileUrl?.name;
		const fileExtension = file?.split(/[#?]/)[0].split(".").pop();
		if (fileExtension === "mp3") {
			return "audiotrack";
		} else {
			return "picture_as_pdf";
		}
	}

	public getFileType(fileUrl?: string | File | null) {
		return isString(fileUrl) && fileUrl.split(/[#?]/)[0].split(".").pop();
	}

	getSlotName(mediaSlotId: MediaSlot) {
		return this.$format(MediaSlotBinding[mediaSlotId]);
	}

	getfileUploadProgress(fileId) {
		const uploadInfo = this.mediaUploadModule.fileUploads.find((f) => f.id === fileId);
		if (uploadInfo) {
			return Number.parseInt(uploadInfo.progress.toFixed(0));
		}
		return 0;
	}

	get isBothVersionNone() {
		return this.selectedGSVersionId === '-1' && this.selectedLSVersionId === '-1';
	}
}

interface WordSpecVpcItemMedia {
	fileUrl?: string | File | null;
	mediaId?: string;
	mediaStatusId?: MediaStatus;
	isMediaReplaced: boolean;
	isMediaRemoved: boolean;
	mediaSlotId: MediaSlot;
	isMediaAdded: boolean;
	vpcId?: number;
	isUploading: boolean;
	fileId?: string;
	size: number;
}

interface WordSpecVpcItem extends Omit<Partial<WordSpecVpcRespnseModel>, "content"> {
	unit?: number;
	versionGroupId?: string;
	versionGroupName: string;
	media: WordSpecVpcItemMedia[];
}
